/**
 * **The Inspect tool** (developer, 2026-09-28): a rail tool that reads the cell under the pointer.
 * While it is armed, the hovered cell gets a readout beside it (`inspectCell.ts`,
 * `InspectReadout.tsx`), and the terrain mechanics groups containing that cell are outlined and
 * labelled even with the overlay off (`pixiCombatBoard.ts`'s `setTerrainMechanicsFocus`).
 *
 * The hovered cell is the one `CombatStage` already reports for the Map key (`onHoverCell`); on
 * touch, which has no hover, a tap reports it, so a tap inspects and a tap elsewhere moves on. The
 * tool is read-only: nothing is dispatched or sent to the room.
 *
 * Esc hides the readout first (layer 2) until the pointer reaches another cell, then disarms
 * (layer 3), like Measure's ruler. Arming is a layer-3 armed thing (`armedSeam.ts`), so it puts
 * down an aim, a Draw tool, a BUILD tool or Measure, and they put it down.
 */
import { useCallback, useState } from "react";
import type { SquareCell } from "@lancer-vtt/domain";
import { useEscEntry, type EscLayers } from "../../../lib/encounter/escLayers";

export type InspectTool = {
  armed: boolean;
  setArmed: (armed: boolean) => void;
  /** The cell to read out, or `null`: disarmed, off the board, or dismissed with Esc. */
  cell: SquareCell | null;
};

const keyOf = (c: SquareCell | null): string | null => (c ? `${c.x},${c.y}` : null);

export function useInspectTool({ esc, hoverCell }: { esc: EscLayers; hoverCell: SquareCell | null }): InspectTool {
  const [armed, setArmed] = useState(false);
  // The cell Esc dismissed; the readout returns once the pointer reaches any other cell.
  const [dismissed, setDismissed] = useState<string | null>(null);
  const hoverKey = keyOf(hoverCell);
  if (dismissed !== null && dismissed !== hoverKey) setDismissed(null);
  const cell = armed && hoverCell && hoverKey !== dismissed ? hoverCell : null;

  const set = useCallback((next: boolean) => {
    setArmed(next);
    setDismissed(null);
  }, []);

  useEscEntry(esc, { layer: "gesture", id: "inspect-readout", active: cell !== null, onDismiss: () => setDismissed(hoverKey) });
  useEscEntry(esc, { layer: "armed", id: "inspect-tool", active: armed, onDismiss: () => set(false) });

  return { armed, setArmed: set, cell };
}
